import { useEffect, useRef, useState } from "react";
import { useCachedState } from "@/hooks/use-cached-state";
import { createFileRoute } from "@tanstack/react-router";
import { CardSkeleton } from "@/components/loading-skeletons";
import { supabase } from "@/integrations/supabase/client";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ImageUpload } from "@/components/image-upload";
import { toast } from "sonner";
import { Save, School, MapPin, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/_authenticated/school")({
  head: () => ({ meta: [{ title: "Profil Sekolah — MI Al-Munajah Admin" }] }),
  component: SchoolPage,
});

type SchoolProfile = {
  id: string; name: string; logo_url: string | null;
  description: string | null; address: string | null;
  phone: string | null; email: string | null; website: string | null;
  maps_url: string | null;
  google_maps_url: string | null;
  latitude: number | null;
  longitude: number | null;
};

function SchoolPage() {
  const [data, setData] = useCachedState<SchoolProfile | null>("school:data", null);
  const [saving, setSaving] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;
    supabase.from("school_profile")
      .select("id,name,logo_url,description,address,phone,email,website,maps_url,google_maps_url,latitude,longitude")
      .limit(1).maybeSingle().then(({ data }) => {
        setData(data as SchoolProfile);
      });
  }, []);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    const link = (data.google_maps_url || "").trim() || null;
    const { error } = await supabase.from("school_profile").update({
      name: data.name,
      logo_url: data.logo_url,
      description: data.description,
      address: data.address,
      phone: data.phone,
      email: data.email,
      website: data.website,
      google_maps_url: link,
      maps_url: link,
      latitude: data.latitude,
      longitude: data.longitude,
    }).eq("id", data.id);
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Profil sekolah disimpan");
  };

  if (!data) return <CardSkeleton lines={6} />;

  const set = <K extends keyof SchoolProfile>(k: K, v: SchoolProfile[K]) => setData({ ...data, [k]: v });

  const mapsLink = (data.google_maps_url || "").trim() || null;
  const hasCoords = data.latitude != null && data.longitude != null;
  const embedSrc = hasCoords
    ? `https://www.google.com/maps?q=${data.latitude},${data.longitude}&hl=id&z=17&output=embed`
    : null;

  return (
    <div>
      <PageHeader title="Profil Sekolah" description="Informasi lembaga yang ditampilkan di aplikasi siswa." />
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 rounded-2xl lg:col-span-1">
          <div className="flex flex-col items-center text-center">
            <div className="w-28 h-28 rounded-3xl bg-primary/10 text-primary flex items-center justify-center overflow-hidden mb-4">
              {data.logo_url ? <img src={data.logo_url} alt="" className="w-full h-full object-cover" /> : <School className="w-12 h-12" />}
            </div>
            <ImageUpload bucket="school" value={data.logo_url} onChange={(url) => set("logo_url", url)} label="Ubah logo" />
            <h2 className="font-bold text-lg mt-4">{data.name}</h2>
            <p className="text-xs text-muted-foreground mt-1">{data.description || "Tambahkan deskripsi sekolah"}</p>
          </div>
        </Card>

        <Card className="p-6 rounded-2xl lg:col-span-2 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nama Sekolah"><Input value={data.name} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Telepon"><Input value={data.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Email"><Input type="email" value={data.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Website"><Input value={data.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://" /></Field>
          </div>
          <Field label="Alamat"><Textarea rows={2} value={data.address || ""} onChange={(e) => set("address", e.target.value)} /></Field>
          <Field label="Deskripsi"><Textarea rows={4} value={data.description || ""} onChange={(e) => set("description", e.target.value)} /></Field>

          <div className="rounded-xl border bg-muted/30 p-4 space-y-3">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-primary" />
              <h3 className="font-semibold text-sm">Lokasi Sekolah</h3>
            </div>

            <Field label="Google Maps URL (Petunjuk Arah)">
              <Input
                value={data.google_maps_url || ""}
                onChange={(e) => set("google_maps_url", e.target.value)}
                placeholder="https://maps.app.goo.gl/..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                Tombol "Petunjuk Arah" di aplikasi akan membuka link ini.
              </p>
            </Field>

            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Latitude">
                <Input
                  type="number"
                  step="any"
                  value={data.latitude ?? ""}
                  onChange={(e) => set("latitude", e.target.value === "" ? null : parseFloat(e.target.value))}
                  placeholder="-6.200000"
                />
              </Field>
              <Field label="Longitude">
                <Input
                  type="number"
                  step="any"
                  value={data.longitude ?? ""}
                  onChange={(e) => set("longitude", e.target.value === "" ? null : parseFloat(e.target.value))}
                  placeholder="106.816666"
                />
              </Field>
            </div>
            <p className="text-xs text-muted-foreground">
              Koordinat digunakan untuk menampilkan preview peta di aplikasi.
            </p>

            {embedSrc && (
              <div className="rounded-lg overflow-hidden border aspect-video">
                <iframe
                  src={embedSrc}
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Preview lokasi sekolah"
                />
              </div>
            )}

            {mapsLink && (
              <Button asChild variant="outline" size="sm" className="rounded-xl">
                <a href={mapsLink} target="_blank" rel="noreferrer">
                  <ExternalLink className="w-4 h-4 mr-1" /> Buka Link Maps
                </a>
              </Button>
            )}
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="rounded-xl"><Save className="w-4 h-4 mr-2" /> Simpan</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}
